/**
 * Helpers to format and parse city/province for display.
 */

import defaultLocations from '../config/configDefaultLocationSearches';

export const cleanProvinceText = text => {
  if (!text || typeof text !== 'string') return text || '';
  return (
    text
      .replace(/^provincia\s+de\s+/i, '')
      .replace(/^província\s+de\s+/i, '')
      .replace(/^province\s+of\s+/i, '')
      .replace(/^province\s+de\s+/i, '')
      .trim() || text
  );
};

export const formatCityProvince = (city, province) => {
  if (!city && !province) return '';
  if (!province) return city || '';
  if (!city) return province || '';
  return `${city}, ${province}`;
};

/**
 * Extract city from a full address string (Mapbox / legacy listings).
 */
export const extractCityFromAddress = address => {
  if (!address) return '';

  const postalCityMatch = address.match(/\b\d{4,5}\s+([^,]+),/);
  if (postalCityMatch?.[1]) return postalCityMatch[1].trim();

  const parts = address.split(',').map(p => p.trim());
  for (const part of parts) {
    const m = part.match(/\b\d{4,5}\s+(.+)/);
    if (m?.[1]) return m[1].trim();
  }
  return '';
};

/**
 * Extract province from a full address string.
 */
export const extractProvinceFromAddress = address => {
  if (!address) return '';

  const parts = address.split(',').map(p => p.trim());
  for (const part of parts) {
    if (/^provincia\s+de\s+/i.test(part) || /^província\s+de\s+/i.test(part)) {
      return cleanProvinceText(part);
    }
  }

  if (parts.length >= 3) {
    const candidate = parts[parts.length - 2];
    if (candidate && !/^\d{4,5}/.test(candidate) && !/^españa$/i.test(candidate)) {
      return cleanProvinceText(candidate);
    }
  }

  return '';
};

export const getListingLocationDisplay = ({ city, province, address }) => {
  const resolvedCity = city || extractCityFromAddress(address);
  const resolvedProvince = province || extractProvinceFromAddress(address);
  return formatCityProvince(resolvedCity, resolvedProvince);
};

/**
 * Resolve a province label from (fuzzed) geolocation by matching the configured
 * default-location searches. Offline stand-in for reverse-geocoding against our
 * known markets — used e.g. by the GTM purchase dataLayer, since listings don't
 * currently carry province in publicData.
 *
 * @param {{ lat: number, lng: number }|null|undefined} geolocation
 * @returns {String} province name (e.g. 'Barcelona') or ''
 */
export const provinceFromGeolocation = geolocation => {
  if (!geolocation || geolocation.lat == null || geolocation.lng == null) {
    return '';
  }
  const { lat, lng } = geolocation;
  const match = defaultLocations.find(loc => {
    const b = loc?.predictionPlace?.bounds;
    return b && lat <= b.ne.lat && lat >= b.sw.lat && lng <= b.ne.lng && lng >= b.sw.lng;
  });
  const address = match?.predictionPlace?.address || '';
  // Configured addresses look like "Barcelona, Barcelona, Spain" → province is
  // the second segment (same as city for the major markets we operate in).
  const parts = address
    .split(',')
    .map(p => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return parts[1];
  }
  return parts[0] || '';
};
